import { Alert, Typography } from 'antd';
import React, { useEffect, useCallback } from 'react';
import {useNavigate} from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PageFooter } from './HomePage';
import TopNav from '../Components/TopNav';
const { Title} = Typography;

const SimplePage = ({title, paragraphs, component=false}) => {
  return (
    <div className='simple-page'>
      <TopNav dark={true} transparent={false} />
      <div className="main-content container m-5 ml-auto mr-auto p-5">
        {component ? (
          <div className='h-100 d-flex flex-column justify-content-center align-items-center'>
            {component}
          </div>
        ) : (
          <>
            <Title level={1} className="font-outfit font-weight-bold">{title}</Title>
            {paragraphs.map((paragraph, index) => (
              <div key={index} className="mt-3 font-outfit term-text">
                {paragraph}
              </div>
            ))}
          </>
        )}
      </div>
      <PageFooter />
    </div>
  );
};

const EmailVerificationPage = () => {
    const { emailVerify } = useAuth();
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [hasError, setHasError] = useState(false);
    const navigate = useNavigate();

    const verifyEmail = useCallback(() => {
        const searchParams = window.location.search;
        console.log(searchParams);
        emailVerify(searchParams).then(res => {
            setLoading(false);
            setHasError(false);
            setMessage(res.data.message);
        }).catch(err => {
            setLoading(false);
            setHasError(true);
            setMessage(err.response.data.message);
        });
    }, [emailVerify]); // Add dependencies if needed

    useEffect(() => {
        verifyEmail();
    }, [verifyEmail]);

    if (loading) {
        return <div>Loading...</div>;
    } else {
        setTimeout(() => {
            navigate('/Dashboard');
        }, 2000);
        return (
            <div className='d-flex flex-column justify-content-center align-items-center vh-100'>
                <div className="container">
                    {hasError ? (
                        <Alert message="Error" description={message} type="error" showIcon />
                    ) : (
                        <Alert message="Success" description={message} type="success" showIcon />
                    )}
                    <h3 className='text-center mt-5'>Redirecting...</h3>
                </div>
            </div>
        );
    }
};

const termsOfServiceParagraphs = [
  // Introduction and Acceptance of Terms
  <h2 key="h1">1. Introduction and Acceptance of Terms</h2>,
  <p key="p1">MenuPortal provides its Service subject to these Terms. If you do not agree to these Terms or are not authorized to bind your organization, you should refrain from accessing or using the Service. MenuPortal reserves the right to update or modify these Terms at any time, with notice of significant changes posted on our website or sent via email. Continued use of the Service after modifications constitutes acceptance of the updated Terms.</p>,

  // Eligibility
  <h2 key="h2">2. Eligibility</h2>,
  <ul key="u1">
    <li><strong>Minimum Age Requirement</strong>: You must be at least 18 years old, or the age of majority in your jurisdiction, to create an account or use the Service.</li>
    <li><strong>Authority and Representation</strong>: By using the Service, you affirm that you have the legal authority to agree to these Terms, either as an individual or as an authorized representative of a business or organization.</li>
  </ul>,

  // Account Registration and Security
  <h2 key="h3">3. Account Registration and Security</h2>,
  <p key="p3">To access the Service, you may be required to create an account, choosing a secure password and providing accurate, complete registration information. By doing so, you agree to:</p>,
  <ul key="u2">
    <li><strong>Provide True Information</strong>: Ensure that all information you provide is accurate and up to date.</li>
    <li><strong>Maintain Account Security</strong>: You are responsible for maintaining the confidentiality of your account credentials and accept responsibility for all activities that occur under your account.</li>
    <li><strong>Notify of Unauthorized Use</strong>: If you become aware of unauthorized access or any breach of security, you must notify MenuPortal promptly.</li>
  </ul>,
  <p key="p4">MenuPortal is not liable for any losses or damages resulting from unauthorized access to your account due to your failure to maintain security.</p>,

  // Access and Usage Rights
  <h2 key="h4">4. Access and Usage Rights</h2>,
  <h3 key="h4-1">4.1 Permitted Uses</h3>,
  <ul key="u3">
    <li>Users may integrate the API's restaurant data into applications for internal or non-commercial development.</li>
    <li><strong>Commercial Use</strong>: For any commercial deployment, resale, or distribution of MenuPortal data, you must obtain prior written authorization from MenuPortal.. Unauthorized commercial use is strictly prohibited, particularly for users in the free sandbox or trial versions.</li>
  </ul>,

  <h3 key="h4-2">4.2 Data Restrictions and Prohibited Actions</h3>,
  <ul key="u4">
    <li><strong>Data Scraping</strong>: Extracting, scraping, or storing data from the Service outside of intended API interactions is prohibited.</li>
    <li><strong>Unlawful or Harmful Activities</strong>: Engaging in activities that violate any law, regulation, or the intellectual property rights of MenuPortal or third parties is prohibited.</li>
    <li><strong>Interference and Exploitation</strong>: Attempting to exploit, harm, or disrupt the Service, such as by using unauthorized APIs, software, bots, or other automated processes is prohibited.</li>
  </ul>,
  <p key="p5">MenuPortal reserves the right to monitor your usage and suspend or terminate access if prohibited activities are detected.</p>,

  <h3 key="h4-3">4.3 API Access Limits and Fair Use Policy</h3>,
  <ul key="u5">
    <li><strong>Rate Limits</strong>: Paid users are provided reasonable access limits but may encounter rate-limiting during periods of high demand to ensure stability for all users.</li>
    <li><strong>Sandbox Access</strong>: The free sandbox provides limited access to fictional data to facilitate testing and development. A paid subscription is required for access to real restaurant data.</li>
  </ul>,

  <h3 key="h4-4">4.4 Fees and Billing Terms</h3>,
  <ul key="u6">
    <li><strong>Subscription Plans</strong>: Users may select from various subscription plans. Fees for each plan are displayed at the time of purchase and are billed according to the selected subscription cycle (monthly, annually, etc.).</li>
    <li><strong>Plan Upgrades and Downgrades</strong>: Changing subscription levels may result in fee adjustments. Consult MenuPortal’s customer support for more information.</li>
  </ul>,

  // Term and Termination
  <h2 key="h5">5. Term and Termination</h2>,
  <h3 key="h5-1">5.1 Subscription Terms</h3>,
  <ul key="u7">
    <li><strong>Auto-Renewal</strong>: All subscriptions automatically renew at the end of each term unless canceled before the renewal date.</li>
    <li><strong>Termination by MenuPortal</strong>: MenuPortal may choose not to renew subscriptions or may terminate access at its discretion upon notice at the end of any term.</li>
  </ul>,

  <h3 key="h5-2">5.2 Long-Term Cancellation Terms</h3>,
  <ul key="u8">
    <li><strong>Multi-Month Plans</strong>: For subscriptions longer than 90 days, MenuPortal reserves the right to terminate the Service with 90 days' written notice. A prorated refund will be issued for any remaining, unused time.</li>
    <li><strong>Custom Arrangements</strong>: Custom or enterprise contracts may include unique termination terms, as outlined in each contract.</li>
  </ul>,

  <h3 key="h5-3">5.3 User-Initiated Termination</h3>,
  <ul key="u9">
    <li>Users may cancel their subscription or account at any time; however, refunds are not provided for any prepaid term except under circumstances outlined in Section 6 regarding Service disruptions.</li>
  </ul>,

  // Service Reliability and Support
  <h2 key="h6">6. Service Reliability and Support</h2>,
  <h3 key="h6-1">6.1 General Support</h3>,
  <ul key="u10">
    <li>MenuPortal offers reasonable support for users but does not guarantee the availability of 24/7 support. Certain support tiers are available for enterprise customers with specific service agreements.</li>
  </ul>,

  <h3 key="h6-2">6.2 Service Disruptions</h3>,
  <ul key="u11">
    <li><strong>Compensation for Downtime</strong>: In the event of an API disruption lasting more than one business day, MenuPortal will issue credits to affected paid users. Credits are proportional to the amount of access time lost and apply toward the current subscription term.</li>
  </ul>,

  <h3 key="h6-3">6.3 Scheduled Maintenance</h3>,
  <ul key="u12">
    <li>MenuPortal may schedule maintenance that affects Service availability. Users will receive advance notice when feasible. During scheduled maintenance, the Service may be temporarily unavailable.</li>
  </ul>,

  // Intellectual Property and Proprietary Rights
  <h2 key="h7">7. Intellectual Property and Proprietary Rights</h2>,
  <h3 key="h7-1">7.1 Ownership of the Service</h3>,
  <ul key="u13">
    <li>MenuPortal retains all ownership and intellectual property rights in the Service, including software, API code, databases, documentation, trademarks, and logos. These rights are protected by intellectual property laws and international treaties.</li>
  </ul>,

  <h3 key="h7-2">7.2 Limited License</h3>,
  <ul key="u14">
    <li>MenuPortal grants users a revocable, non-transferable, non-exclusive license to access and use the Service, as long as the usage aligns with these Terms.</li>
    <li><strong>Restrictions</strong>: Users may not modify, distribute, sublicense, or reverse engineer any aspect of the Service or its data without express permission from MenuPortal.</li>
  </ul>,

  // Privacy and Data Protection
  <h2 key="h8">8. Privacy and Data Protection</h2>,
  <p key="p6">MenuPortal collects, uses, and stores user data according to our Privacy Policy, which is incorporated by reference into these Terms. By using the Service, you agree to the collection and processing of personal data as outlined in the Privacy Policy. Users should:</p>,
  <ul key="u15">
    <li><strong>Confidentiality</strong>: Respect the confidentiality of any non-public data accessed through the Service.</li>
    <li><strong>Compliance with Applicable Privacy Laws</strong>: Ensure compliance with all relevant privacy and data protection regulations.</li>
  </ul>,

  // Disclaimer of Warranties
  <h2 key="h9">9. Disclaimer of Warranties</h2>,
  <ul key="u16">
    <li><strong>“As-Is” Service</strong>: The Service is provided on an “as-is” and “as-available” basis. MenuPortal expressly disclaims all warranties, whether express, implied, statutory, or otherwise, including but not limited to any implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</li>
    <li><strong>No Guarantee of Accuracy</strong>: MenuPortal does not guarantee the accuracy, completeness, or reliability of the data provided.</li>
  </ul>,

  // Limitation of Liability
  <h2 key="h10">10. Limitation of Liability</h2>,
  <ul key="u17">
    <li><strong>Maximum Liability</strong>: To the maximum extent permitted by law, MenuPortal’s liability shall be limited to the fees paid by the user in the six months preceding the claim.</li>
    <li><strong>Exclusion of Certain Damages</strong>: MenuPortal shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data, revenue, profits, or goodwill arising from or related to these Terms or your use of the Service.</li>
  </ul>,

  // Indemnification
  <h2 key="h11">11. Indemnification</h2>,
  <p key="p7">You agree to indemnify, defend, and hold harmless MenuPortal, its officers, directors, employees, and agents from any and all claims, liabilities, damages, losses, or expenses arising from your breach of these Terms, unauthorized use of the Service, or violation of any third-party rights, including intellectual property rights.</p>,

  // Export Compliance
  <h2 key="h12">12. Export Compliance</h2>,
  <ul key="u18">
    <li><strong>Compliance Requirement</strong>: Users agree to comply with all applicable laws, including export and import regulations, regarding the distribution and use of the Service and data. Users are responsible for determining the applicability of these laws.</li>
  </ul>,

  // Dispute Resolution and Governing Law
  <h2 key="h13">13. Dispute Resolution and Governing Law</h2>,
  <h3 key="h13-1">13.1 Governing Law</h3>,
  <ul key="u19">
    <li>These Terms shall be governed by the laws of the jurisdiction in which MenuPortal operates.</li>
  </ul>,

  <h3 key="h13-2">13.2 Jurisdiction and Venue</h3>,
  <ul key="u20">
    <li>Any disputes arising from or related to these Terms or the Service shall be resolved exclusively in the courts of this jurisdiction.</li>
  </ul>,

  <h3 key="h13-3">13.3 Alternative Dispute Resolution</h3>,
  <ul key="u21">
    <li>For certain disputes, you and MenuPortal may agree to pursue mediation or arbitration. Alternative dispute resolution methods are encouraged to resolve matters efficiently.</li>
  </ul>,

  // Miscellaneous Provisions
  <h2 key="h14">14. Miscellaneous Provisions</h2>,
  <h3 key="h14-1">14.1 Entire Agreement</h3>,
  <ul key="u22">
    <li>These Terms, including any incorporated documents, represent the entire agreement between you and MenuPortal regarding the use of the Service.</li>
  </ul>,

  <h3 key="h14-2">14.2 Severability</h3>,
  <ul key="u23">
    <li>If any provision of these Terms is deemed unlawful, void, or unenforceable, the remaining provisions shall remain in effect.</li>
  </ul>,

  <h3 key="h14-3">14.3 No Waiver</h3>,
  <ul key="u24">
    <li>The failure of MenuPortal to enforce any provision of these Terms shall not be deemed a waiver of its right to enforce that provision or any other provision.</li>
  </ul>,

  <h3 key="h14-4">14.4 Assignment</h3>,
  <ul key="u25">
    <li>You may not assign your rights or obligations under these Terms without MenuPortal’s prior written consent. MenuPortal may assign its rights and obligations at its discretion.</li>
  </ul>,

  // User Responsibilities and Acceptable Use Policy
  <h2 key="h15">15. User Responsibilities and Acceptable Use Policy</h2>,
  <ul key="u26">
    <li><strong>Compliance with Laws</strong>: Users are responsible for ensuring their use of the Service complies with all applicable laws and regulations, including intellectual property and data protection laws.</li>
    <li><strong>Account Conduct</strong>: Users must respect system stability, not share login credentials with unauthorized parties, and refrain from activities that would harm MenuPortal’s operations.</li>
  </ul>,

  // API Key Management and Security
  <h2 key="h16">16. API Key Management and Security</h2>,
  <ul key="u27">
    <li><strong>API Key Confidentiality</strong>: Users are responsible for securely managing their API keys and will be held accountable for any actions taken with their keys.</li>
    <li><strong>Revocation</strong>: MenuPortal reserves the right to revoke or suspend API keys if a user violates the Terms or misuses the Service.</li>
  </ul>,

  // Data Ownership and User Content
  <h2 key="h17">17. Data Ownership and User Content</h2>,
  <ul key="u28">
    <li><strong>User-Provided Data</strong>: If users submit, upload, or provide data via the Service (e.g., for testing or analytics), they retain ownership but grant MenuPortal a limited, revocable license to use the data to operate and improve the Service.</li>
    <li><strong>Compliance and Data Deletion</strong>: MenuPortal’s procedures for data deletion and compliance if a user requests deletion or upon termination of the Service.</li>
  </ul>,

  // Third-Party Services and Integrations
  <h2 key="h18">18. Third-Party Services and Integrations</h2>,
  <ul key="u29">
    <li><strong>Third-Party Dependencies</strong>: MenuPortal is not responsible for the reliability, functionality, or availability of third-party services that the Service relies on.</li>
    <li><strong>User Responsibility</strong>: Users may be responsible for complying with any additional terms imposed by third-party services if they integrate with them through MenuPortal.</li>
  </ul>,

  // Force Majeure
  <h2 key="h19">19. Force Majeure</h2>,
  <ul key="u30">
    <li><strong>Uncontrollable Events</strong>: MenuPortal is not liable for delays or failures in Service delivery due to events beyond its control, such as natural disasters, internet outages, or governmental actions.</li>
  </ul>,

  // Beta Features
  <h2 key="h20">20. Beta Features</h2>,
  <ul key="u31">
    <li><strong>Experimental Access</strong>: Beta features or pre-release services are provided as-is, may contain bugs, and are subject to change. Users acknowledge that beta features may not be fully supported.</li>
  </ul>,

  // Notice and Communication
  <h2 key="h21">21. Notice and Communication</h2>,
  <ul key="u32">
    <li><strong>Contact Method</strong>: MenuPortal will communicate important notices to users through email, dashboard notifications, or the website. Users should keep their contact information current.</li>
    <li><strong>User Communications</strong>: MenuPortal has the right to use suggestions or feedback provided by users to improve the Service.</li>
  </ul>
];
    

const TermsOfService = () => {  
  return (
    <SimplePage title="Terms of Service" paragraphs={termsOfServiceParagraphs} />
  );
};


const PrivacyPolicyParagraphs = [
  // Introduction
  <h2 key="h1">1. Introduction</h2>,
  <p key="p1">This Privacy Policy describes how MenuPortal (“we,” “us,” or “our”) collects, uses, discloses, and protects personal information in relation to its API service, which delivers restaurant data for integration into other applications. This Policy applies to all users who access or interact with the MenuPortal API and affiliated services. By using our services, you acknowledge and agree to the data practices described in this Privacy Policy.</p>,

  // Information We Collect
  <h2 key="h2">2. Information We Collect</h2>,
  <p key="p2">We collect and process several types of data to operate and improve the MenuPortal API, including:</p>,
  <ul key="u1">
    <li><strong>User Data</strong>: Information needed to create and manage accounts, authenticate users, and support account administration, such as:
      <ul>
        <li><strong>Account Information</strong>: Names, email addresses, usernames, passwords, and API keys.</li>
        <li><strong>Billing Information</strong>: Payment details, including credit card information and billing addresses, managed through secure, third-party payment processors.</li>
        <li><strong>Usage Data</strong>: Information related to API interactions, such as request logs and usage statistics, collected for performance monitoring, support, and service optimization.</li>
        <li><strong>Support Data</strong>: Information provided by users via support tickets or inquiries to assist with troubleshooting and service enhancement.</li>
      </ul>
    </li>
    <li><strong>Technical Data</strong>: Automatically collected technical details about devices and interactions with the MenuPortal API or website, including:
      <ul>
        <li><strong>Device and Browser Information</strong>: IP addresses, browser types, operating systems, and unique device identifiers.</li>
        <li><strong>Cookies and Tracking Technologies</strong>: Cookies and similar technologies used for authentication, analytics, and to enhance user experience on our website.</li>
      </ul>
    </li>
    <li><strong>Restaurant Data</strong>: Data about restaurants, such as menus, hours, and public information, provided through our API for use in applications. This data is strictly business-related, with no user-generated content or personal information beyond publicly accessible business data.</li>
  </ul>,

  // How We Use Information
  <h2 key="h3">3. How We Use Information</h2>,
  <p key="p3">We use collected data for the following purposes:</p>,
  <ul key="u2">
    <li><strong>Account Management and Authentication</strong>: To create and manage user accounts, enable secure API access, and support customer interactions.</li>
    <li><strong>Service Provision and Performance</strong>: To deliver the API services, monitor usage, maintain performance standards, and troubleshoot technical issues.</li>
    <li><strong>Billing and Payments</strong>: To manage paid subscriptions and process payments, working with PCI-compliant third-party payment processors to ensure secure handling of financial information.</li>
    <li><strong>Customer Support and Communication</strong>: To respond to inquiries, provide technical support, and communicate updates or relevant information about the service.</li>
    <li><strong>Analytics and Service Improvements</strong>: To analyze API usage and improve our offerings. Aggregated data is used to understand general usage trends and optimize service functionality.</li>
  </ul>,

  // Information Sharing and Disclosure
  <h2 key="h4">4. Information Sharing and Disclosure</h2>,
  <p key="p4">MenuPortal is committed to protecting user privacy and limits data sharing to specific circumstances:</p>,
  <ul key="u3">
    <li><strong>Service Providers</strong>: We may share necessary data with trusted third-party vendors who provide essential services, such as payment processing, infrastructure hosting, and technical support. These providers are contractually obligated to protect your data and use it solely for the purpose of providing services to MenuPortal.</li>
    <li><strong>Aggregated and Anonymized Data</strong>: For business insights and analytics, we may share aggregated, non-identifiable data with partners or publish it for reporting purposes.</li>
    <li><strong>Legal Obligations</strong>: We may disclose personal information if required to comply with legal processes, such as court orders or subpoenas, or to protect MenuPortal’s rights and the security of our services.</li>
    <li><strong>Business Transfers</strong>: In the event of a merger, acquisition, or asset sale, personal data may be transferred as part of the transaction. We will notify users if their personal data is subject to such a transfer and becomes subject to a new privacy policy.</li>
  </ul>,

  // International Data Transfers
  <h2 key="h5">5. International Data Transfers</h2>,
  <p key="p5">MenuPortal may transfer and store personal information in countries outside your own, including in the United States or other countries where our third-party service providers operate. When transferring data internationally, we ensure it is protected by using legally approved mechanisms, such as Standard Contractual Clauses (SCCs), to maintain an adequate level of protection for personal data as required by relevant data protection laws.</p>,

  // Data Retention
  <h2 key="h6">6. Data Retention</h2>,
  <p key="p6">We retain personal data only as long as it is necessary to provide our services or to fulfill legal or business obligations:</p>,
  <ul key="u4">
    <li><strong>Account and Billing Information</strong>: Retained as long as the user has an active account. Upon account deletion, all non-billing-related data is erased within 30 days.</li>
    <li><strong>Usage Data</strong>: Retained for a minimum of one year to monitor service quality, ensure compliance, and enhance security.</li>
    <li><strong>Deletion Requests</strong>: Users may request deletion of their data, subject to legal and business requirements. Deletion requests are processed within 30 days, with only essential billing and financial records retained as required by law.</li>
  </ul>,

  // Data Security
  <h2 key="h7">7. Data Security</h2>,
  <p key="p7">We employ a range of security measures to protect your personal data against unauthorized access, loss, or alteration:</p>,
  <ul key="u5">
    <li><strong>Data Encryption</strong>: Sensitive data is encrypted both in transit and at rest.</li>
    <li><strong>Access Controls</strong>: Strict access controls limit access to personal data, with role-based permissions to enhance privacy protection.</li>
    <li><strong>Regular Security Audits</strong>: Our infrastructure undergoes regular security reviews and updates to ensure the highest standards of data protection.</li>
  </ul>,
  <p key="p8">Despite these precautions, no transmission or storage system can guarantee absolute security, and we encourage users to take steps to protect their own account information.</p>,

  // User Rights and Choices
  <h2 key="h8">8. User Rights and Choices</h2>,
  <p key="p9">MenuPortal respects users’ rights concerning their personal data and provides several ways to manage information:</p>,
  <ul key="u6">
    <li><strong>Access and Update</strong>: Users may request access to their personal data or request corrections by contacting our support team.</li>
    <li><strong>Data Portability</strong>: Users can request their data in a commonly used, machine-readable format.</li>
    <li><strong>Data Deletion</strong>: Users may request account deletion and data removal, which will be processed within 30 days, with all non-essential data permanently erased.</li>
    <li><strong>Opt-Out of Tracking</strong>: Where applicable, users may adjust browser settings to control cookie preferences or opt out of specific data-sharing practices.</li>
  </ul>,

  // Children’s Privacy
  <h2 key="h9">9. Children’s Privacy</h2>,
  <p key="p10">MenuPortal services are not directed at children under 16, and we do not knowingly collect or solicit personal information from individuals in this age group. If we become aware of any such data collected unintentionally, we will take prompt action to delete it.</p>,

  // Automated Decision-Making
  <h2 key="h10">10. Automated Decision-Making</h2>,
  <p key="p11">MenuPortal may use automated processes for service-related functions, such as identifying unusual API usage to prevent security breaches. No automated processing results in decisions that have legal or significant effects on users, and data used for monitoring is anonymized and aggregated.</p>,

  // Legal Bases for Processing (GDPR)
  <h2 key="h11">11. Legal Bases for Processing (GDPR)</h2>,
  <p key="p12">For users within the European Union, we rely on the following legal bases for processing personal data, in accordance with the General Data Protection Regulation (GDPR):</p>,
  <ul key="u7">
    <li><strong>Contractual Necessity</strong>: Data processing for account creation, service provision, and billing.</li>
    <li><strong>User Consent</strong>: For optional data collection (e.g., marketing communications), we seek and rely on user consent.</li>
    <li><strong>Legitimate Interests</strong>: Processing data for purposes of security, performance analysis, and service improvements where it is in both our interests and does not override user rights.</li>
    <li><strong>Legal Compliance</strong>: When necessary, to fulfill legal obligations.</li>
  </ul>,

  // Compliance with Privacy Laws
  <h2 key="h12">12. Compliance with Privacy Laws</h2>,
  <ul key="u8">
    <li><strong>General Data Protection Regulation (GDPR)</strong>: MenuPortal complies with GDPR for users in the European Union, providing rights to data access, deletion, portability, and more.</li>
    <li><strong>California Consumer Privacy Act (CCPA)</strong>: MenuPortal respects the CCPA for California residents, offering transparency and control over data collection and sharing practices.</li>
    <li><strong>Other Jurisdictions</strong>: MenuPortal seeks to comply with applicable data privacy laws in all regions where it operates.</li>
  </ul>,

  // Changes to this Privacy Policy
  <h2 key="h13">13. Changes to this Privacy Policy</h2>,
  <p key="p13">MenuPortal may update this Privacy Policy to reflect changes in our practices or legal obligations. Material changes will be communicated via email or a prominent notice on our website. We encourage users to review this Privacy Policy periodically to stay informed about our data practices.</p>,

  // Contact Us
  <h2 key="h14">14. Contact Us</h2>,
  <p key="p14">If you have questions about this Privacy Policy, your rights, or MenuPortal’s data practices, please contact us at:</p>,
  <p key="p15"><strong>MenuPortal Privacy Team</strong><br/><a href="mailto:contact@mail.apix.site">contact@mail.apix.site</a></p>
]

const PrivacyPolicy = () => {
  return (
    <SimplePage title="Privacy Policy" paragraphs={PrivacyPolicyParagraphs} />
  );
};

export { PrivacyPolicy, SimplePage, TermsOfService, EmailVerificationPage };